import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../api/client';
import { activitySeed, eventSeed, memberSeed, paymentSeed, userSeed } from './mockSeed';
import * as membersApi from '../api/members';
import * as eventsApi from '../api/events';
import * as paymentsApi from '../api/payments';
import * as usersApi from '../api/users';
import * as dashboardApi from '../api/dashboard';

const DataContext = createContext(null);

// ── Mock-mode helpers ────────────────────────────────────────────────────
function nextNumericId(prefix, items) {
  const max = items.reduce((acc, item) => {
    const match = String(item.id || '').match(/(\d+)\s*$/);
    if (!match) return acc;
    const n = parseInt(match[1], 10);
    return n > acc ? n : acc;
  }, 0);
  const width = items.length && items[0].id ? Math.max(4, String(max).length) : 4;
  return `${prefix}-${String(max + 1).padStart(width, '0')}`;
}

function eventNextId(items) {
  const year = new Date().getFullYear();
  const ofYear = items.filter((e) => String(e.id).includes(`EVT-${year}-`));
  const max = ofYear.reduce((acc, item) => {
    const match = String(item.id).match(/(\d+)$/);
    if (!match) return acc;
    const n = parseInt(match[1], 10);
    return n > acc ? n : acc;
  }, 0);
  return `EVT-${year}-${String(max + 1).padStart(3, '0')}`;
}

// ── Provider ─────────────────────────────────────────────────────────────
export function DataProvider({ children }) {
  // In API mode we seed the collections empty and replace them with backend
  // data on mount. In mock mode we keep the original seed so design dev
  // works without a backend.
  const [members, setMembers] = useState(useApi ? [] : memberSeed);
  const [events, setEvents] = useState(useApi ? [] : eventSeed);
  const [payments, setPayments] = useState(useApi ? [] : paymentSeed);
  const [users, setUsers] = useState(useApi ? [] : userSeed);
  const [activity, setActivity] = useState(useApi ? [] : activitySeed);
  const [loading, setLoading] = useState(useApi);
  const [error, setError] = useState(null);

  // Track whether the initial fetch finished. We mount the provider above
  // the auth boundary, so the first paint is the public LoginPage; refetch
  // once a token shows up. Listening on the storage key keeps this honest
  // across tabs too.
  const initialFetchDone = useRef(false);

  const refreshAll = useCallback(async () => {
    if (!useApi) return;
    if (!localStorage.getItem('izm.token')) return;
    setLoading(true);
    setError(null);
    try {
      const [membersPage, eventsPage, paymentsPage, usersPage, activityList] = await Promise.all([
        membersApi.listMembers().catch((e) => ({ content: [], _err: e })),
        eventsApi.listEvents().catch((e) => ({ content: [], _err: e })),
        paymentsApi.listPayments().catch((e) => ({ content: [], _err: e })),
        usersApi.listUsers().catch((e) => ({ content: [], _err: e })),
        dashboardApi.getActivity().catch(() => []),
      ]);
      setMembers(membersPage.content);
      setEvents(eventsPage.content);
      setPayments(paymentsPage.content);
      setUsers(usersPage.content);
      setActivity(activityList);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      initialFetchDone.current = true;
    }
  }, []);

  useEffect(() => {
    if (!useApi) return;
    refreshAll();
    const onStorage = (ev) => {
      if (ev.key === 'izm.token' && ev.newValue) refreshAll();
    };
    window.addEventListener('storage', onStorage);
    // The AuthContext fires this when login succeeds in the same tab.
    const onAuthChange = () => refreshAll();
    window.addEventListener('izm:auth-changed', onAuthChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('izm:auth-changed', onAuthChange);
    };
  }, [refreshAll]);

  // Activity push — mock-mode synthesises an entry; API mode lets the backend
  // be the source of truth, so we just refetch quietly.
  const pushActivity = useCallback((entry) => {
    if (useApi) {
      dashboardApi.getActivity().then(setActivity).catch(() => {});
      return;
    }
    setActivity((prev) => [
      { id: `a-${Date.now()}`, at: new Date().toISOString(), dot: 'green', ...entry },
      ...prev,
    ].slice(0, 20));
  }, []);

  // ── Members ──────────────────────────────────────────────────────
  const createMember = useCallback(async (data) => {
    if (useApi) {
      const saved = await membersApi.createMember(data);
      setMembers((prev) => [saved, ...prev]);
      pushActivity({ textKey: 'activity.new_member', params: { name: `${saved.firstName} ${saved.lastName}` }, dot: 'green' });
      return saved;
    }
    const id = nextNumericId('MEM', members);
    const next = { ...data, id };
    setMembers((prev) => [next, ...prev]);
    pushActivity({ textKey: 'activity.new_member', params: { name: `${data.firstName} ${data.lastName}` }, dot: 'green' });
    return next;
  }, [members, pushActivity]);

  const updateMember = useCallback(async (id, patch) => {
    if (useApi) {
      const saved = await membersApi.updateMember(id, patch);
      setMembers((prev) => prev.map((m) => (m.id === id ? saved : m)));
      return saved;
    }
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const deleteMember = useCallback(async (id) => {
    if (useApi) {
      await membersApi.deleteMember(id);
    }
    setMembers((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // ── Events ───────────────────────────────────────────────────────
  const createEvent = useCallback(async (data) => {
    if (useApi) {
      const saved = await eventsApi.createEvent(data);
      setEvents((prev) => [saved, ...prev]);
      pushActivity({ textKey: 'activity.event_updated', params: { name: saved.name }, dot: 'blue' });
      return saved;
    }
    const id = eventNextId(events);
    const next = { ...data, id, registered: data.registered ?? 0 };
    setEvents((prev) => [next, ...prev]);
    pushActivity({ textKey: 'activity.event_updated', params: { name: data.name }, dot: 'blue' });
    return next;
  }, [events, pushActivity]);

  const updateEvent = useCallback(async (id, patch) => {
    if (useApi) {
      const saved = await eventsApi.updateEvent(id, patch);
      setEvents((prev) => prev.map((e) => (e.id === id ? saved : e)));
      return saved;
    }
    setEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }, []);

  const deleteEvent = useCallback(async (id) => {
    if (useApi) {
      await eventsApi.deleteEvent(id);
    }
    setEvents((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // ── Payments ─────────────────────────────────────────────────────
  const createPayment = useCallback(async (data) => {
    if (useApi) {
      const saved = await paymentsApi.createPayment(data);
      setPayments((prev) => [saved, ...prev]);
      if (saved.status === 'paid') {
        pushActivity({ textKey: 'activity.payment_confirmed', params: { ref: saved.code || saved.id }, dot: 'gold' });
      }
      return saved;
    }
    const id = nextNumericId('PAY', payments);
    const next = { ...data, id };
    setPayments((prev) => [next, ...prev]);
    if (next.status === 'paid') {
      pushActivity({ textKey: 'activity.payment_confirmed', params: { ref: id }, dot: 'gold' });
    }
    return next;
  }, [payments, pushActivity]);

  const updatePayment = useCallback(async (id, patch) => {
    if (useApi) {
      const current = payments.find((p) => p.id === id);
      // PATCH-style requests aren't supported — the backend takes a full
      // PaymentRequest. Merge the existing entity with the incoming patch.
      const merged = { ...current, ...patch };
      let saved;
      if (Object.keys(patch).length === 1 && patch.status === 'paid') {
        saved = await paymentsApi.markPaymentPaid(id);
      } else {
        saved = await paymentsApi.updatePayment(id, merged);
      }
      setPayments((prev) => prev.map((p) => (p.id === id ? saved : p)));
      if (saved.status === 'paid' && (!current || current.status !== 'paid')) {
        pushActivity({ textKey: 'activity.payment_confirmed', params: { ref: saved.code || saved.id }, dot: 'gold' });
      }
      return saved;
    }
    setPayments((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    if (patch.status === 'paid') {
      pushActivity({ textKey: 'activity.payment_confirmed', params: { ref: id }, dot: 'gold' });
    }
  }, [payments, pushActivity]);

  const deletePayment = useCallback(async (id) => {
    if (useApi) {
      await paymentsApi.deletePayment(id);
    }
    setPayments((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // ── Users ────────────────────────────────────────────────────────
  const createUser = useCallback(async (data) => {
    if (useApi) {
      const saved = await usersApi.createUser(data);
      setUsers((prev) => [saved, ...prev]);
      return saved;
    }
    const id = nextNumericId('USR', users);
    const next = { ...data, id, active: true, lastLoginAt: null };
    setUsers((prev) => [next, ...prev]);
    return next;
  }, [users]);

  const updateUser = useCallback(async (id, patch) => {
    if (useApi) {
      const saved = await usersApi.updateUser(id, patch);
      setUsers((prev) => prev.map((u) => (u.id === id ? saved : u)));
      return saved;
    }
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const setUserActive = useCallback(async (id, active) => {
    if (useApi) {
      const saved = active ? await usersApi.activateUser(id) : await usersApi.deactivateUser(id);
      setUsers((prev) => prev.map((u) => (u.id === id ? saved : u)));
      return saved;
    }
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active } : u)));
  }, []);

  // ── Lookups ──────────────────────────────────────────────────────
  const memberById = useCallback((id) => members.find((m) => m.id === id), [members]);
  const eventById = useCallback((id) => events.find((e) => e.id === id), [events]);

  const memberTotalPaid = useCallback(
    (memberId) => {
      // In API mode the backend already exposes `totalPaid` on the member;
      // fall back to summing locally if it's missing (mock seed has no such
      // pre-computed field, neither does an older backend response).
      const m = members.find((x) => x.id === memberId);
      if (m && typeof m.totalPaid === 'number' && m.totalPaid > 0) return m.totalPaid;
      return payments
        .filter((p) => p.memberId === memberId && p.status === 'paid')
        .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
    },
    [members, payments]
  );

  const value = useMemo(
    () => ({
      members,
      events,
      payments,
      users,
      activity,
      loading,
      error,
      refreshAll,
      createMember,
      updateMember,
      deleteMember,
      createEvent,
      updateEvent,
      deleteEvent,
      createPayment,
      updatePayment,
      deletePayment,
      createUser,
      updateUser,
      setUserActive,
      memberById,
      eventById,
      memberTotalPaid,
    }),
    [
      members,
      events,
      payments,
      users,
      activity,
      loading,
      error,
      refreshAll,
      createMember,
      updateMember,
      deleteMember,
      createEvent,
      updateEvent,
      deleteEvent,
      createPayment,
      updatePayment,
      deletePayment,
      createUser,
      updateUser,
      setUserActive,
      memberById,
      eventById,
      memberTotalPaid,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
